import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { costInputTypesApi } from '../../utils/api';
import SearchableSelect from '../SearchableSelect';
import { ChevronIcon } from '../icons';
import { GRID_WITH_COST, fmtUSD, fmtUSDc, fmtQty } from './helpers';

// PRD-024 / PRD-020 F5 — per-Outcome Estimated Cost panel (seller-only).
// Catalog-linked Cost Type lines share the SAME 8-column grid template as the
// economics line, so each line's cost lands directly under the Est. cost
// column (col 6), the per-Outcome subtotal closes the group in that column, and
// the footer total continues it — one traceable column. This is the Proposal-
// stage (estimated-only) panel; the Agreement stage renders Est→Actual side by
// side in Worksheet's AgreementCostLines. No inline Cost-Type creation — Cost
// Types come from the Cost Type page.

// PRD-027 WU-G — short date for a rate-window breakdown row (e.g. "Jun 1").
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function fmtDay(s) {
  if (!s) return 'open';
  const [, m, d] = s.split('-');
  return `${MONTHS[+m - 1]} ${+d}`;
}

export default function CostEstimatePanel({
  proposalId, outcomeId, costLines = [], estimatedCostCents = 0,
  editable, onAdd, onRemove,
}) {
  const [adding, setAdding] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [pickId, setPickId] = useState(null);
  const [pickName, setPickName] = useState('');
  const [qty, setQty] = useState('');
  const [busy, setBusy] = useState(false);
  // PRD-027 WU-G — expanded per-window composite breakdowns.
  const [openBreak, setOpenBreak] = useState(() => new Set());
  const toggleBreak = (id) => setOpenBreak((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  useEffect(() => {
    if (!adding || catalog.length) return;
    costInputTypesApi.list()
      .then((r) => setCatalog((r.data.costInputTypes || []).map((c) => ({
        id: c.id, name: c.catalog_name || c.name || 'Cost Type',
      }))))
      .catch(() => setCatalog([]));
  }, [adding, catalog.length]);

  const grid = { display: 'grid', gridTemplateColumns: GRID_WITH_COST, gap: '12px', alignItems: 'center' };

  const submit = async () => {
    if (!pickId || !qty) return;
    setBusy(true);
    try {
      await onAdd(outcomeId, { costInputTypeId: pickId, estimatedQuantity: parseFloat(qty) });
      setAdding(false); setPickId(null); setPickName(''); setQty('');
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-amber-50/40 border-t border-b border-amber-100 px-4 py-2">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 mb-1 flex items-center gap-2">
        Cost estimate
        <span className="text-[9px] font-semibold text-amber-700 bg-amber-100 border border-amber-200 rounded px-1.5 py-0.5 normal-case tracking-normal">agency-only</span>
      </div>

      {costLines.length === 0 && !adding && (
        <p className="text-xs text-gray-600 py-0.5">No cost lines yet.</p>
      )}

      {costLines.map((line) => {
        const open = openBreak.has(line.id);
        const composite = line.composite && (line.window_parts || []).length > 1;
        return (
          <React.Fragment key={line.id}>
            <div style={grid} className="py-0.5">
              <div style={{ gridColumn: '1 / 5' }} className="flex items-center gap-1.5 text-xs text-gray-600 min-w-0 pl-4">
                <span className="w-1.5 h-1.5 rounded-sm bg-amber-400 shrink-0" />
                <span className="truncate">{line.cost_type_name}</span>
                <span className="text-gray-600 tabular-nums shrink-0">
                  · {fmtQty(line.estimated_quantity)}{composite ? '' : ` × ${fmtUSDc(line.rate_cents)}`}
                </span>
              </div>
              <div style={{ gridColumn: 6 }} className="text-right text-xs text-amber-800 font-semibold tabular-nums">
                {line.gap ? <span className="text-red-600 font-bold">no rate</span> : fmtUSD(line.line_cost_cents)}
                {/* PRD-027 WU-G — composite / flat / single-window indicator. */}
                <div className="mt-0.5">
                  {line.gap ? (
                    // PRD-028 follow-up — name the uncovered date(s) so it reads as a
                    // date-range mismatch, not a missing rate.
                    <span className="inline-block text-[9px] font-bold text-red-700 bg-red-100 rounded px-1.5 py-0.5"
                      title={line.gap_label ? `This Outcome's active window includes ${line.gap_label}, which no rate schedule window covers. Extend a rate window or adjust the dates.` : 'The active window includes dates no rate schedule window covers.'}>
                      {line.gap_label ? `no rate · ${line.gap_label}` : 'no rate window'}
                    </span>
                  ) : line.is_flat ? (
                    <span className="inline-block text-[9px] font-bold text-gray-600 bg-gray-100 rounded px-1.5 py-0.5">flat · 1 rate</span>
                  ) : composite ? (
                    <button onClick={() => toggleBreak(line.id)} className="inline-flex items-center gap-1 text-[9px] font-extrabold text-amber-900 bg-amber-300 rounded px-1.5 py-0.5">
                      <span className={`inline-flex transition-transform ${open ? 'rotate-90' : ''}`}><ChevronIcon /></span>{line.window_parts.length} rates
                    </button>
                  ) : (
                    <span className="inline-block text-[9px] font-bold text-gray-600 bg-gray-100 rounded px-1.5 py-0.5">1 rate</span>
                  )}
                </div>
              </div>
              {editable && (
                <div style={{ gridColumn: 8 }} className="text-right">
                  <button className="text-gray-600 hover:text-red-600 text-sm" title="Remove" onClick={() => onRemove(outcomeId, line.id)}>×</button>
                </div>
              )}
            </div>
            {composite && open && (
              <div className="pl-10 pr-4 pb-1.5">
                {line.window_parts.map((p, i) => (
                  <div key={i} className="flex justify-between items-center gap-3 py-0.5 border-t border-amber-100 first:border-t-0">
                    <div className="text-[11px] text-gray-600 tabular-nums">
                      <b className="text-gray-700">{fmtDay(p.valid_from)} – {fmtDay(p.valid_to)}</b>
                      {' · '}{fmtQty(Math.round(p.sub_quantity))} units<span className="text-amber-700 font-semibold ml-1">× {fmtUSDc(p.rate_cents)}</span>
                    </div>
                    <div className="text-[11px] font-bold text-amber-800 tabular-nums">{fmtUSD(p.sub_cost_cents)}</div>
                  </div>
                ))}
                <div className="text-[10px] text-gray-600 mt-1">Time-prorated across the Outcome's active window. One rate valid at a time; frozen as a composite at signing.</div>
              </div>
            )}
          </React.Fragment>
        );
      })}

      {/* Per-Outcome subtotal in the Est. cost column (col 6). */}
      <div style={grid} className="py-1 border-t border-amber-100 mt-0.5">
        <div style={{ gridColumn: '1 / 6' }} className="text-right text-[10px] font-semibold uppercase tracking-wide text-amber-700">Outcome est. cost</div>
        <div style={{ gridColumn: 6 }} className="text-right text-xs font-bold text-amber-900 tabular-nums">{fmtUSD(estimatedCostCents)}</div>
      </div>

      {editable && (adding ? (
        <div className="py-1.5 flex flex-wrap items-end gap-2">
          <div className="min-w-[220px]">
            <SearchableSelect
              options={catalog}
              value={pickId}
              onChange={(id2, name2) => { setPickId(id2); setPickName(name2); }}
              placeholder="Select a Cost Type…"
            />
          </div>
          <input
            className="input w-28" type="number" step="any" min="0" placeholder="Est. qty"
            value={qty} onChange={(e) => setQty(e.target.value)}
          />
          <button className="btn-primary text-sm" disabled={busy || !pickId || !qty} onClick={submit}>Add</button>
          <button className="btn-secondary text-sm" onClick={() => { setAdding(false); setPickId(null); setQty(''); }}>Cancel</button>
        </div>
      ) : (
        <div className="py-1">
          <button className="text-xs font-semibold text-amber-700 hover:text-amber-800" onClick={() => setAdding(true)}>+ Add Cost Type</button>
        </div>
      ))}

      <div className="text-[10px] text-gray-600">
        Cost Types come from <Link to="/cost-input-types" className="text-amber-700 hover:text-amber-800">your Cost Type page</Link> (catalog-linked).
      </div>
    </div>
  );
}
